import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs } from "@expert/shared-types";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { trackFullStoryEvent } from "@expert/monitoring";
import { Box, Divider, Group, Popover, Portal, Stack, Text, UnstyledButton } from "@mantine/core";
import { useClipboard, useTextSelection, useWindowEvent } from "@mantine/hooks";
import { IconCheck, IconCopy, IconSparkles } from "@tabler/icons-react";
import { type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { useInvokeExpertAssist } from "../../hooks/useSendExpertAssistMessage";
import { askAssistAboutHighlight, type HighlightAssistReply as Reply } from "./askAssistAboutHighlight";
import { reportHighlightAsk } from "./reportHighlightAsk";
import { TextHighlightAnswer } from "./TextHighlightAnswer";
import classes from "./TextHighlightArea.module.css";

export type TextHighlightSurface = "transcript" | "timeline" | "guide";

const AREA_ATTRIBUTE = "data-text-highlight-area";
// Lets the selection settle (keyboard selection fires a change per keypress) before asking Assist about it.
const SETTLE_DELAY_MS = 250;
// Every highlight of at least this many characters asks Assist, including double-clicks and copy-only highlights.
// Accepted for the pilot; volume is tracked through MessageSent with `source: "TextHighlight"`.
const MIN_HIGHLIGHT_LENGTH = 2;
// Only the question is capped; Copy always copies the whole selection.
const MAX_QUESTION_LENGTH = 500;
const COPIED_RESET_MS = 1500;
const LOADING: Reply = { status: "loading" };
const VIEWPORT_PADDING = 16;
// The side is picked once on open and kept (`preventPositionChangeWhenVisible`); when the answer loads, `size` caps
// the menu at the space left on that side, and the answer scrolls rather than the menu moving.
const POPOVER_MIDDLEWARES = {
    flip: true,
    shift: { padding: VIEWPORT_PADDING },
    size: { padding: VIEWPORT_PADDING },
};

interface Highlight {
    text: string;
    rect: DOMRect;
    id: string;
    reply: Reply;
}

export interface TextHighlightAreaProps {
    children: ReactNode;
    surface: TextHighlightSurface;
    logger: Logger;
    gaiaPayloadProps: GaiaPayloadConfigs;
    isEnabled?: boolean;
}

/**
 * Highlighting text inside `children` opens a menu at the selection to copy it, and asks Assist to "tell me more"
 * about it, showing the answer in the menu once it lands. The question and answer stay out of the AI Assist timeline.
 */
export function TextHighlightArea({ isEnabled = true, children, ...props }: TextHighlightAreaProps): JSX.Element {
    if (!isEnabled) return <>{children}</>;
    return <TextHighlightWatcher {...props}>{children}</TextHighlightWatcher>;
}

function TextHighlightWatcher({
    children,
    surface,
    logger,
    gaiaPayloadProps,
}: Omit<TextHighlightAreaProps, "isEnabled">) {
    const areaRef = useRef<HTMLDivElement>(null);
    const selection = useTextSelection();
    const [isPointerDown, setIsPointerDown] = useState(false);
    const [highlight, setHighlight] = useState<Highlight | null>(null);
    // A click that leaves the selection in place (e.g. on a scrollbar) re-runs the effect; don't ask twice.
    const askedTextRef = useRef("");
    const invoke = useInvokeExpertAssist(logger, gaiaPayloadProps);
    const { dispatcher } = useReactAnalytics();
    // Stops listening for the open highlight's reply once it's closed or replaced.
    const stopAskRef = useRef<() => void>(undefined);
    // The open highlight's text, to keep the menu pinned to it.
    const rangeRef = useRef<Range>(undefined);

    useWindowEvent("pointerup", () => setIsPointerDown(false));

    // `useTextSelection` hands back the same Selection object on every change, so key the effect on its text.
    const selectedText = selection?.toString().trim() ?? "";

    useEffect(() => {
        if (selectedText.length < MIN_HIGHLIGHT_LENGTH) askedTextRef.current = "";
        if (isPointerDown || selectedText.length < MIN_HIGHLIGHT_LENGTH || !selection?.rangeCount) return;
        if (selectedText === askedTextRef.current) return;
        const range = selection.getRangeAt(0);
        if (!isInArea(range.commonAncestorContainer, areaRef.current)) return;

        const timer = setTimeout(() => {
            askedTextRef.current = selectedText;
            const id = crypto.randomUUID();
            rangeRef.current = range.cloneRange();
            setHighlight({ text: selectedText, rect: range.getBoundingClientRect(), id, reply: LOADING });

            stopAskRef.current?.();
            const report = reportHighlightAsk(dispatcher, surface);
            stopAskRef.current = askAssistAboutHighlight({
                invoke,
                text: selectedText.slice(0, MAX_QUESTION_LENGTH),
                onSent: report.onSent,
                onSettled: report.onSettled,
                onReply: (reply) => setHighlight((current) => (current?.id === id ? { ...current, reply } : current)),
            });
        }, SETTLE_DELAY_MS);
        return () => clearTimeout(timer);
        // `selection` is a stable reference and `invoke` is rebuilt every render; neither should re-ask Assist.
        // oxlint-disable-next-line exhaustive-deps
    }, [selectedText, isPointerDown, surface]);

    const close = useCallback(() => {
        stopAskRef.current?.();
        stopAskRef.current = undefined;
        rangeRef.current = undefined;
        setHighlight(null);
    }, []);
    useEffect(() => () => stopAskRef.current?.(), []);

    // Keep the menu pinned to the text as the panel scrolls; close once the text scrolls out of the list.
    const reposition = useCallback(() => {
        const range = rangeRef.current;
        if (!range) return;
        const rect = range.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) close();
        else setHighlight((current) => (current ? { ...current, rect } : current));
    }, [close]);
    useWindowEvent("scroll", reposition, { capture: true, passive: true });
    useWindowEvent("resize", reposition);

    return (
        <>
            <div
                className={classes.area}
                ref={areaRef}
                onPointerDown={() => setIsPointerDown(true)}
                {...{ [AREA_ATTRIBUTE]: surface }}
            >
                {children}
            </div>
            {highlight && (
                <TextHighlightMenu highlight={highlight} surface={surface} onClose={close} key={highlight.id} />
            )}
        </>
    );
}

interface TextHighlightMenuProps {
    highlight: Highlight;
    surface: TextHighlightSurface;
    onClose: () => void;
}

function TextHighlightMenu({ highlight, surface, onClose }: TextHighlightMenuProps) {
    const { copied, copy } = useClipboard({ timeout: COPIED_RESET_MS });
    const { dispatcher } = useReactAnalytics();
    const { rect, text, id, reply } = highlight;

    const onCopy = () => {
        copy(text);
        void dispatcher.dispatchBusinessEvent("Click_TextHighlightCopy", { surface, highlightId: id });
        trackFullStoryEvent("Click_TextHighlightCopy", { surface, highlightId: id });
    };

    return (
        <Popover
            opened
            onDismiss={onClose}
            position="bottom-start"
            offset={8}
            width={280}
            shadow="md"
            radius="md"
            middlewares={POPOVER_MIDDLEWARES}
            preventPositionChangeWhenVisible
            returnFocus={false}
            withinPortal
        >
            <Portal>
                <Popover.Target>
                    <Box
                        className={classes.anchor}
                        top={rect.top}
                        left={rect.left}
                        w={rect.width}
                        h={rect.height}
                        aria-hidden
                    />
                </Popover.Target>
            </Portal>
            <Popover.Dropdown className={classes.dropdown} data-testid="text-highlight-menu">
                <Stack className={classes.dropdownBody} gap={0}>
                    <UnstyledButton className={classes.item} onClick={onCopy} data-testid="text-highlight-copy">
                        <Group gap="xs" wrap="nowrap">
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                            <Text size="sm">{copied ? "Copied" : "Copy"}</Text>
                        </Group>
                    </UnstyledButton>
                    <Divider className={classes.divider} />
                    <Stack className={classes.tellMeMore} gap="3xs">
                        <Group gap="4xs" wrap="nowrap" c="dark.2">
                            <IconSparkles size={12} className={classes.sparkle} />
                            <Text size="xs" c="dark.2" lineClamp={1} className="fs-mask">
                                {`Tell me more about "${text}"`}
                            </Text>
                        </Group>
                        <div className={classes.replySlot}>
                            <TextHighlightAnswer reply={reply} />
                        </div>
                    </Stack>
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}

/** In this area (not a nested one, and not an input the expert is typing in). */
function isInArea(node: Node, area: HTMLElement | null): boolean {
    const element = node instanceof Element ? node : node.parentElement;
    if (!element || !area) return false;
    if (element.closest("input, textarea, [contenteditable='true']")) return false;
    return element.closest(`[${AREA_ATTRIBUTE}]`) === area;
}
